import {
  createStyles,
  Group,
  Indicator,
  Navbar,
  ScrollArea,
  Tooltip,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  ChartBar,
  Checklist,
  File3d,
  Hierarchy2,
  Icon as TablerIcon,
  Logout,
  Map2,
  Notes,
  Settings,
  ShoppingCart,
  Truck,
  TruckDelivery,
  TruckLoading,
  Users,
} from 'tabler-icons-react';
import React, { useContext, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthProvider';
import { ROLE_ENUM, ROLE_ENUM_VALUES } from '../api_enums/ROLE_ENUM';
import { useTranslation } from 'react-i18next';
import {
  InquiryContext,
  OrderPositionContext,
} from '../context/ContextWrapper';
import { INQUIRY_STATUS_ENUM } from '../api_enums/INQUIRY_STATUS_ENUM';
import { ORDER_STATUS_ENUM } from '../api_enums/ORDER_STATUS_ENUM';
import ISection from '../models/ISection';
import IUser from '../models/IUser';
import IOrderPosition from '../models/IOrder';
import IInquiry from '../models/IInquiry';

interface NavbarLinkProps {
  icon: TablerIcon;
  label: string;
  path: string;
  color: string;
  alertCount?: number;
}

const useStyles = createStyles((theme) => ({
  link: {
    width: 50,
    height: 50,
    borderRadius: theme.radius.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color:
      theme.colorScheme === 'dark'
        ? theme.colors.dark[0]
        : theme.colors.gray[7],

    '&:hover': {
      backgroundColor:
        theme.colorScheme === 'dark'
          ? theme.colors.dark[5]
          : theme.colors.gray[0],
    },
  },

  standard_active: {
    '&, &:hover': {
      backgroundColor:
        theme.colorScheme === 'dark'
          ? theme.fn.rgba(theme.colors[theme.primaryColor][8], 0.25)
          : theme.colors[theme.primaryColor][1],
      color:
        theme.colors[theme.primaryColor][theme.colorScheme === 'dark' ? 4 : 7],
    },
  },

  red_active: {
    '&, &:hover': {
      backgroundColor:
        theme.colorScheme === 'dark'
          ? theme.fn.rgba(theme.colors['red'][9], 0.25)
          : theme.colors['red'][0],
      color: theme.colors['red'][theme.colorScheme === 'dark' ? 4 : 7],
    },
  },

  orange_active: {
    '&, &:hover': {
      backgroundColor:
        theme.colorScheme === 'dark'
          ? theme.fn.rgba(theme.colors['orange'][9], 0.25)
          : theme.colors['orange'][0],
      color: theme.colors['orange'][theme.colorScheme === 'dark' ? 4 : 7],
    },
  },

  yellow_active: {
    '&, &:hover': {
      backgroundColor:
        theme.colorScheme === 'dark'
          ? theme.fn.rgba(theme.colors['yellow'][9], 0.25)
          : theme.colors['yellow'][0],
      color: theme.colors['yellow'][theme.colorScheme === 'dark' ? 4 : 7],
    },
  },

  lime_active: {
    '&, &:hover': {
      backgroundColor:
        theme.colorScheme === 'dark'
          ? theme.fn.rgba(theme.colors['lime'][9], 0.25)
          : theme.colors['lime'][0],
      color: theme.colors['lime'][theme.colorScheme === 'dark' ? 4 : 7],
    },
  },

  green_active: {
    '&, &:hover': {
      backgroundColor:
        theme.colorScheme === 'dark'
          ? theme.fn.rgba(theme.colors['green'][9], 0.25)
          : theme.colors['green'][0],
      color: theme.colors['green'][theme.colorScheme === 'dark' ? 4 : 7],
    },
  },

  black_active: {
    '&, &:hover': {
      backgroundColor:
        theme.colorScheme === 'dark'
          ? theme.fn.rgba(theme.colors['dark'][9], 0.25)
          : theme.colors['dark'][0],
      color: theme.colors['dark'][theme.colorScheme === 'dark' ? 4 : 7],
    },
  },

  grape_active: {
    '&, &:hover': {
      backgroundColor:
        theme.colorScheme === 'dark'
          ? theme.fn.rgba(theme.colors['grape'][9], 0.25)
          : theme.colors['grape'][0],
      color: theme.colors['grape'][theme.colorScheme === 'dark' ? 4 : 7],
    },
  },

  cyan_active: {
    '&, &:hover': {
      backgroundColor:
        theme.colorScheme === 'dark'
          ? theme.fn.rgba(theme.colors['cyan'][9], 0.25)
          : theme.colors['cyan'][0],
      color: theme.colors['cyan'][theme.colorScheme === 'dark' ? 4 : 7],
    },
  },

  blue_active: {
    '&, &:hover': {
      backgroundColor:
        theme.colorScheme === 'dark'
          ? theme.fn.rgba(theme.colors['blue'][9], 0.25)
          : theme.colors['blue'][0],
      color: theme.colors['blue'][theme.colorScheme === 'dark' ? 4 : 7],
    },
  },

  teal_active: {
    '&, &:hover': {
      backgroundColor:
        theme.colorScheme === 'dark'
          ? theme.fn.rgba(theme.colors['teal'][9], 0.25)
          : theme.colors['teal'][0],
      color: theme.colors['teal'][theme.colorScheme === 'dark' ? 4 : 7],
    },
  },
}));

function getColorStyle(color, classes) {
  if (color === 'red') return classes.red_active;
  if (color === 'orange') return classes.orange_active;
  if (color === 'yellow') return classes.yellow_active;
  if (color === 'lime') return classes.lime_active;
  if (color === 'green') return classes.green_active;
  if (color === 'black') return classes.black_active;
  if (color === 'grape') return classes.grape_active;
  if (color === 'cyan') return classes.cyan_active;
  if (color === 'blue') return classes.blue_active;
  if (color === 'teal') return classes.teal_active;
  return classes.standard_active;
}

function NavbarLink({
  icon: Icon,
  label,
  path,
  color,
  alertCount,
}: NavbarLinkProps) {
  const { classes, cx } = useStyles();
  const loc = useLocation();
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm}px)`);

  const linkContent = (
    <Link to={path}>
      <Indicator
        inline
        size={20}
        offset={10}
        position="top-end"
        color="red"
        label={alertCount}
        disabled={!alertCount || alertCount === 0}
        withBorder
      >
        <UnstyledButton
          className={cx(classes.link, {
            [getColorStyle(color, classes)]: loc.pathname === `/${path}`,
          })}
        >
          <Icon size={30} />
        </UnstyledButton>
      </Indicator>
    </Link>
  );

  // Auf mobilen Geräten kein Tooltip anzeigen
  if (isMobile) {
    return linkContent;
  }

  return (
    <Tooltip.Floating label={label} position="right">
      {linkContent}
    </Tooltip.Floating>
  );
}

export function AppNavbar() {
  const { t } = useTranslation();
  const { logout, hasRight, userInfo } = useContext(AuthContext);
  const inquiryContext = useContext(InquiryContext);
  const orderPositionContext = useContext(OrderPositionContext);

  // Berechne Alert-Counts für verschiedene NavbarLinks
  const inquiredCount = useMemo(() => {
    return (
      inquiryContext?.entities?.filter(
        (inquiry) =>
          ((inquiry?.is_waiting_at as ISection)?.id === userInfo?.section_id ||
            (inquiry?.inquired_by as IUser)?.id === userInfo?.user_id ||
            !userInfo.section_supervisor_id) &&
          ((inquiry?.status as INQUIRY_STATUS_ENUM) ===
            INQUIRY_STATUS_ENUM.INQUIRED ||
            (inquiry?.status as INQUIRY_STATUS_ENUM) ===
              INQUIRY_STATUS_ENUM.IN_APPROVAL),
      ).length || 0
    );
  }, [inquiryContext?.entities]);

  const approvedInquiriesCount = useMemo(() => {
    return (
      inquiryContext?.entities?.filter(
        (inquiry) =>
          ((inquiry?.is_waiting_at as ISection)?.id === userInfo?.section_id ||
            (inquiry?.inquired_by as IUser)?.id === userInfo?.user_id) &&
          (inquiry?.status as INQUIRY_STATUS_ENUM) ===
            INQUIRY_STATUS_ENUM.APPROVED,
      ).length || 0
    );
  }, [inquiryContext?.entities]);

  const approvedOrdersCount = useMemo(() => {
    return (
      orderPositionContext?.entities?.filter(
        (order) =>
          ((
            ((order as IOrderPosition)?.inquired_through as IInquiry)
              ?.ordered_from as ISection
          )?.id === userInfo.section_id ||
            userInfo.section_roles.includes(ROLE_ENUM.MANAGER) ||
            userInfo.section_roles.includes(ROLE_ENUM.ADMINISTRATOR)) &&
          [
            ORDER_STATUS_ENUM.PICKING,
            ORDER_STATUS_ENUM.PARTIALLY_DISPATCHED,
            ORDER_STATUS_ENUM.PARTIALLY_DELIVERED,
            ORDER_STATUS_ENUM.PARTIALLY_LOADED,
            ORDER_STATUS_ENUM.NOT_DELIVERED,
          ].includes(order.status as ORDER_STATUS_ENUM) &&
          order.remain_loading != 0,
      ).length || 0
    );
  }, [orderPositionContext?.entities]);

  const navElements = [
    {
      icon: ChartBar,
      label: t('AppNavbar.Statistics'),
      path: '',
      color: ROLE_ENUM_VALUES.get(ROLE_ENUM.REQUESTOR).color,
      roles: [ROLE_ENUM.USER],
    },
    {
      icon: Notes,
      label: t('AppNavbar.Requirements'),
      path: 'inquiries',
      color: ROLE_ENUM_VALUES.get(ROLE_ENUM.REQUESTOR).color,
      roles: [ROLE_ENUM.REQUESTOR],
    },
    {
      icon: Checklist,
      label: t('AppNavbar.ReleaseRequirements'),
      path: 'inquiries_approve',
      color: ROLE_ENUM_VALUES.get(ROLE_ENUM.APPROVER).color,
      roles: [ROLE_ENUM.APPROVER],
      alertCount: inquiredCount,
    },
    {
      icon: ShoppingCart,
      label: t('AppNavbar.OrderRequirements'),
      path: 'inquiries_purchase',
      color: ROLE_ENUM_VALUES.get(ROLE_ENUM.PURCHASER).color,
      roles: [ROLE_ENUM.PURCHASER],
      alertCount: approvedInquiriesCount,
    },
    {
      icon: TruckDelivery,
      label: t('AppNavbar.SendOrders'),
      path: 'orders_ship',
      color: ROLE_ENUM_VALUES.get(ROLE_ENUM.SUPPLIER).color,
      roles: [ROLE_ENUM.SHIPPER, ROLE_ENUM.SUPPLIER, ROLE_ENUM.LOGISTICAN],
      alertCount: approvedOrdersCount,
    },
    {
      icon: Map2,
      label: t('AppNavbar.DeliveryLocations'),
      path: 'locations',
      color: ROLE_ENUM_VALUES.get(ROLE_ENUM.REQUESTOR).color,
      roles: [ROLE_ENUM.REQUESTOR, ROLE_ENUM.RECIPIENT],
    },
    {
      icon: Truck,
      label: t('AppNavbar.Vehicles'),
      path: 'vehicles',
      color: ROLE_ENUM_VALUES.get(ROLE_ENUM.LOGISTICAN).color,
      roles: [ROLE_ENUM.LOGISTICAN],
    },
    {
      icon: File3d,
      label: t('AppNavbar.Resource'),
      path: 'resources',
      color: ROLE_ENUM_VALUES.get(ROLE_ENUM.SUPPLIER).color,
      roles: [ROLE_ENUM.SUPPLIER],
    },
    {
      icon: TruckLoading,
      label: t('AppNavbar.PackagingUnit'),
      path: 'packageunit',
      color: ROLE_ENUM_VALUES.get(ROLE_ENUM.SUPPLIER).color,
      roles: [ROLE_ENUM.SUPPLIER, ROLE_ENUM.LOGISTICAN],
    },
    {
      icon: Users,
      label: t('AppNavbar.Users'),
      path: 'users',
      color: ROLE_ENUM_VALUES.get(ROLE_ENUM.MANAGER).color,
      roles: [ROLE_ENUM.MANAGER],
    },
    {
      icon: Settings,
      label: t('AppNavbar.Settings'),
      path: 'settings',
      color: ROLE_ENUM_VALUES.get(ROLE_ENUM.USER).color,
      roles: [ROLE_ENUM.USER],
    },
    {
      icon: Hierarchy2,
      label: t('AppNavbar.OperationalSections'),
      path: 'sections',
      color: ROLE_ENUM_VALUES.get(ROLE_ENUM.MANAGER).color,
      roles: [ROLE_ENUM.USER, ROLE_ENUM.MANAGER],
    },
  ];

  const links = navElements.map((link) =>
    hasRight(link.roles) ? (
      <NavbarLink
        {...link}
        key={link.label}
        path={link.path}
        color={link.color}
      />
    ) : (
      <></>
    ),
  );

  const { classes, cx } = useStyles();
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm}px)`);

  const logoutButton = (
    <UnstyledButton className={cx(classes.link)} onClick={logout}>
      <Logout color="red" size={30} />
    </UnstyledButton>
  );

  return (
    <Navbar width={{ base: 85 }} p="md">
      <Navbar.Section grow component={ScrollArea} mx="-xs" px="xs">
        <Group align="center" spacing={0}>
          {links}
        </Group>
      </Navbar.Section>
      <Navbar.Section>
        <Group align="center" spacing={0}>
          {isMobile ? (
            logoutButton
          ) : (
            <Tooltip
              label={t('AppNavbar.Logout')}
              position="right"
              withArrow
              transitionDuration={0}
            >
              {logoutButton}
            </Tooltip>
          )}
        </Group>
      </Navbar.Section>
    </Navbar>
  );
}
